/**
 * Project Modal - Create/Edit Project
 * Modal form to capture project metadata for creating or editing a project.
 * Supports both create and edit modes via the `project` prop.
 */

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { ErrorFallback } from '~/components/ErrorBoundary/ErrorFallback';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { type ProjectMetadata, useProjectStore } from '~/hooks/useProjectStore';
import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';

export interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** If provided, modal opens in edit mode for this project */
  project?: {
    id: string;
    name: string;
    clientName?: string;
    targetCompletionDate?: string;
  };
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, onClose, project }) => {
  const navigate = useNavigate();
  const createProject = useProjectStore((state) => state.createProject);
  const updateProjectMetadata = useProjectStore((state) => state.updateProjectMetadata);

  const isEditMode = !!project;

  // Form state
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [targetCompletionDate, setTargetCompletionDate] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset/populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (project) {
        // Edit mode: populate with existing values
        setProjectName(project.name || '');
        setClientName(project.clientName || '');
        // Convert ISO date to YYYY-MM-DD for input
        setTargetCompletionDate(
          project.targetCompletionDate ? project.targetCompletionDate.split('T')[0] : ''
        );
      } else {
        // Create mode: reset form
        setProjectName('');
        setClientName('');
        setTargetCompletionDate('');
      }
      setErrors([]);
    }
  }, [isOpen, project]);

  // Check if form is valid (required fields filled)
  const isFormValid = projectName.trim() !== '' && clientName.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const newErrors: string[] = [];
    if (!projectName.trim()) {
      newErrors.push('Project name is required');
    }
    if (!clientName.trim()) {
      newErrors.push('Client name is required');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && project) {
        // Update existing project
        const updates: Partial<ProjectMetadata> = {
          name: projectName.trim(),
          clientName: clientName.trim(),
          targetCompletionDate: targetCompletionDate
            ? new Date(targetCompletionDate).toISOString()
            : undefined,
        };

        await updateProjectMetadata(project.id, updates);
        onClose();
      } else {
        // Create new project
        const projectId = await createProject({
          id: crypto.randomUUID(), // Placeholder - server generates real ID
          name: projectName.trim(),
          clientName: clientName.trim(),
          targetCompletionDate: targetCompletionDate
            ? new Date(targetCompletionDate).toISOString()
            : undefined,
        });

        if (!projectId) {
          setErrors(['Failed to create project. Please try again.']);
          setIsSubmitting(false);
          return;
        }

        onClose();
        // Navigate to setup page using server-generated ID
        navigate(`/projects/${projectId}/${PROJECT_ROUTES.SETUP}`);
      }
    } catch {
      setErrors([`Failed to ${isEditMode ? 'update' : 'create'} project. Please try again.`]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <ErrorBoundary fallback={<ErrorFallback section="project form" />}>
      <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} placement="center">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxWidth={LAYOUT_SPACING.MAX_WIDTH_FORM}>
            <Dialog.Header>
              <Dialog.Title>{isEditMode ? 'Edit Project' : 'Create New Project'}</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <VStack gap={LAYOUT_SPACING.SECTION_GAP} align="stretch">
                {/* Description */}
                <Text color="gray.600" fontSize="sm">
                  {isEditMode
                    ? 'Update the project information below.'
                    : 'Enter the basic information for your bond calculator project.'}
                </Text>

                {/* Error Display */}
                {errors.length > 0 && (
                  <Box
                    bg="red.50"
                    border="1px solid"
                    borderColor="red.200"
                    borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
                    p={LAYOUT_SPACING.CARD_PADDING}
                  >
                    <Text color="red.700" fontWeight="medium" mb={LAYOUT_SPACING.ELEMENT_GAP}>
                      Please fix the following errors:
                    </Text>
                    <VStack align="start" gap={1}>
                      {errors.map((error) => (
                        <Text key={error} color="red.600" fontSize="sm">
                          • {error}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} id="project-form">
                  <Stack gap={LAYOUT_SPACING.CONTENT_GAP}>
                    {/* Project Name */}
                    <Field.Root required>
                      <Field.Label>Project Name</Field.Label>
                      <Input
                        value={projectName}
                        onChange={(e) => {
                          setProjectName(e.target.value);
                          setErrors([]);
                        }}
                        placeholder="e.g., 2025 School Building Bond"
                        autoFocus={!isEditMode}
                      />
                      <Field.HelperText>A descriptive name for your bond project</Field.HelperText>
                    </Field.Root>

                    {/* Client Name */}
                    <Field.Root required>
                      <Field.Label>Client Name</Field.Label>
                      <Input
                        value={clientName}
                        onChange={(e) => {
                          setClientName(e.target.value);
                          setErrors([]);
                        }}
                        placeholder="e.g., Ann Arbor Public Schools"
                      />
                      <Field.HelperText>The client or organization name</Field.HelperText>
                    </Field.Root>

                    {/* Target Completion Date */}
                    <Field.Root>
                      <Field.Label>Target Completion Date</Field.Label>
                      <Input
                        type="date"
                        value={targetCompletionDate}
                        onChange={(e) => {
                          setTargetCompletionDate(e.target.value);
                          setErrors([]);
                        }}
                      />
                      <Field.HelperText>Optional due date for the project</Field.HelperText>
                    </Field.Root>
                  </Stack>
                </form>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justify="flex-end" gap={LAYOUT_SPACING.BUTTON_GAP_LG} w="full">
                <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  form="project-form"
                  disabled={!isFormValid || isSubmitting}
                  loading={isSubmitting}
                >
                  {isEditMode ? 'Save Changes' : 'Create Project'}
                </Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </ErrorBoundary>
  );
};

// Backwards-compatible export for create-only usage
export const CreateProjectModal: React.FC<{ isOpen: boolean; onClose: () => void }> = (props) => (
  <ProjectModal {...props} />
);
